import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFinancialCascade } from '@/hooks/use-financial-data';
import { cn } from '@/lib/utils';
import type {
  FinancialCascadeLine,
  FinancialCascadeResponse,
  FinancialCascadeView,
  FinancialDerivedCashflow,
} from '@/types/financial';

interface FinancialCascadePanelProps {
  entityId?: string;
  periodId?: string;
  view: FinancialCascadeView;
  showDetailRows?: boolean;
}

interface CascadeRow {
  key: string;
  label: string;
  amountCents: number | null;
  amountDollars: number | null;
  kind: 'line' | 'subtotal' | 'total';
  notes?: string | null;
}

const SUMMARY_ROWS: Array<{
  key: string;
  label: string;
  centsKey: keyof FinancialCascadeResponse['cascade'];
  dollarsKey: keyof FinancialCascadeResponse['cascade'];
  kind: CascadeRow['kind'];
}> = [
  { key: 'operating-income', label: 'Operating Income', centsKey: 'operatingIncomeCents', dollarsKey: 'operatingIncomeDollars', kind: 'subtotal' },
  { key: 'gross-profit', label: 'Gross Profit', centsKey: 'grossProfitCents', dollarsKey: 'grossProfitDollars', kind: 'subtotal' },
  { key: 'other-income', label: 'Other Income', centsKey: 'otherIncomeCents', dollarsKey: 'otherIncomeDollars', kind: 'subtotal' },
  { key: 'net-other-income', label: 'Net Other Income', centsKey: 'netOtherIncomeCents', dollarsKey: 'netOtherIncomeDollars', kind: 'subtotal' },
  { key: 'operating-expenses', label: 'Operating Expenses', centsKey: 'operatingExpensesCents', dollarsKey: 'operatingExpensesDollars', kind: 'subtotal' },
  { key: 'adjusted-ebitda', label: 'Adjusted EBITDA', centsKey: 'ebitdaCents', dollarsKey: 'ebitdaDollars', kind: 'total' },
  { key: 'ebit', label: 'EBIT', centsKey: 'ebitCents', dollarsKey: 'ebitDollars', kind: 'subtotal' },
  { key: 'npbt', label: 'NPBT', centsKey: 'npbtCents', dollarsKey: 'npbtDollars', kind: 'subtotal' },
  { key: 'npat', label: 'NPAT', centsKey: 'npatCents', dollarsKey: 'npatDollars', kind: 'total' },
];

function formatLineLabel(line: FinancialCascadeLine): string {
  if (line.customLabel) return line.customLabel;
  return line.lineItem
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDollars(value: number | null): string {
  if (value === null) return 'n/b';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
}

function buildRows(data: FinancialCascadeResponse, showDetailRows: boolean): CascadeRow[] {
  const detailRows = showDetailRows
    ? data.lines.map((line, index) => ({
        key: `line-${line.lineItem}-${line.customLabel ?? ''}-${index}`,
        label: formatLineLabel(line),
        amountCents: line.amountCents,
        amountDollars: line.amountDollars,
        kind: 'line' as const,
        notes: line.notes,
      }))
    : [];

  const summaryRows = SUMMARY_ROWS
    .map((row) => ({
      key: row.key,
      label: row.label,
      amountCents: data.cascade[row.centsKey] as number | null,
      amountDollars: data.cascade[row.dollarsKey] as number | null,
      kind: row.kind,
    }))
    .filter((row) => row.key !== 'net-other-income' || row.amountCents !== null);

  return [...detailRows, ...summaryRows];
}

function buildDerivedCashflowRows(derivedCashflow: FinancialDerivedCashflow): CascadeRow[] {
  return [
    {
      key: 'operating-cashflow',
      label: 'Operating Cash Flow',
      amountCents: derivedCashflow.operatingCashflowCents,
      amountDollars: derivedCashflow.operatingCashflowCents === null ? null : derivedCashflow.operatingCashflowCents / 100,
      kind: 'subtotal',
    },
    {
      key: 'investing-cashflow',
      label: 'Investing Cash Flow',
      amountCents: derivedCashflow.investingCashflowCents,
      amountDollars: derivedCashflow.investingCashflowCents / 100,
      kind: 'line',
    },
    {
      key: 'financing-cashflow',
      label: 'Financing Cash Flow',
      amountCents: derivedCashflow.financingCashflowCents,
      amountDollars: derivedCashflow.financingCashflowCents / 100,
      kind: 'line',
    },
    {
      key: 'div7a-movement',
      label: 'Div7A Movement',
      amountCents: derivedCashflow.div7aMovementCents,
      amountDollars: derivedCashflow.div7aMovementCents / 100,
      kind: 'line',
    },
    {
      key: 'free-cashflow',
      label: 'Free Cash Flow',
      amountCents: derivedCashflow.freeCashflowCents,
      amountDollars: derivedCashflow.freeCashflowCents === null ? null : derivedCashflow.freeCashflowCents / 100,
      kind: 'total',
    },
    {
      key: 'reconciled-closing-cash',
      label: 'Reconciled Closing Cash',
      amountCents: derivedCashflow.reconciledClosingCashCents,
      amountDollars: derivedCashflow.reconciledClosingCashCents === null ? null : derivedCashflow.reconciledClosingCashCents / 100,
      kind: 'subtotal',
    },
    {
      key: 'face-bs-closing-cash',
      label: 'Face BS Closing Cash',
      amountCents: derivedCashflow.faceClosingCashCents,
      amountDollars: derivedCashflow.faceClosingCashCents / 100,
      kind: 'subtotal',
    },
    {
      key: 'cash-variance',
      label: 'Cash Variance',
      amountCents: derivedCashflow.cashVarianceCents,
      amountDollars: derivedCashflow.cashVarianceCents === null ? null : derivedCashflow.cashVarianceCents / 100,
      kind: derivedCashflow.varianceFlag ? 'total' : 'line',
      notes: derivedCashflow.cashVariancePct === null ? null : formatPercent(derivedCashflow.cashVariancePct),
    },
  ];
}

export const FinancialCascadePanel: React.FC<FinancialCascadePanelProps> = ({
  entityId,
  periodId,
  view,
  showDetailRows = true,
}) => {
  const { data, isLoading, error } = useFinancialCascade(entityId, periodId, view);

  if (!entityId) {
    return null;
  }

  if (isLoading) {
    return (
      <Card data-testid="financial-cascade-panel">
        <CardContent className="py-6 text-sm text-muted-foreground">Loading cascade...</CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card data-testid="financial-cascade-panel">
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <AlertCircle className="h-4 w-4" />
          No verified cascade is available for this period.
        </CardContent>
      </Card>
    );
  }

  const rows = buildRows(data, showDetailRows);
  const derivedCashflowRows = data.derivedCashflow
    ? buildDerivedCashflowRows(data.derivedCashflow)
    : [];

  return (
    <Card data-testid="financial-cascade-panel">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base font-medium">Financial Position Cascade</CardTitle>
          <div className="flex items-center gap-2">
            {data.legacyBadge && <Badge variant="outline">Legacy</Badge>}
            {data.cascade.annualised && <Badge variant="secondary">Annualised</Badge>}
            <Badge variant="outline">{data.period.periodLabel}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Line</TableHead>
              <TableHead className="text-right">Dollars</TableHead>
              <TableHead className="text-right">Cents</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.key}
                className={cn(
                  row.kind === 'subtotal' && 'bg-muted/40 font-medium',
                  row.kind === 'total' && 'bg-muted font-semibold',
                )}
              >
                <TableCell>{row.label}</TableCell>
                <TableCell className="text-right tabular-nums">{formatDollars(row.amountDollars)}</TableCell>
                <TableCell className="text-right tabular-nums">{row.amountCents ?? 'n/b'}</TableCell>
                <TableCell className="text-muted-foreground">{row.notes ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.derivedCashflow && (
          <section
            className="mt-6 border-t pt-4"
            data-testid="financial-derived-cashflow"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-medium">Derived Cash Flow</h3>
              {data.derivedCashflow.varianceFlag && (
                <Badge variant="destructive">{'>5% cash variance'}</Badge>
              )}
            </div>
            {data.derivedCashflow.unavailableReason && (
              <p className="text-[var(--refusal-fg)]">{data.derivedCashflow.unavailableReason}</p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Line</TableHead>
                  <TableHead className="text-right">Dollars</TableHead>
                  <TableHead className="text-right">Cents</TableHead>
                  <TableHead>Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {derivedCashflowRows.map((row) => (
                  <TableRow
                    key={row.key}
                    className={cn(
                      row.kind === 'subtotal' && 'bg-muted/40 font-medium',
                      row.kind === 'total' && 'bg-muted font-semibold',
                    )}
                  >
                    <TableCell>{row.label}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.amountDollars === null ? 'Unavailable' : formatDollars(row.amountDollars)}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.amountCents ?? 'Unavailable'}</TableCell>
                    <TableCell className="text-muted-foreground">{row.notes ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        )}
      </CardContent>
    </Card>
  );
};
